import { backofficeWhoami } from "@/api/query/auth/auth";
import { useBreakGlass } from "@/app/break-glass";
import { ApiError, getStoredToken, setToken } from "@/shared/api-fetch";
import { clearActivity, flagLockedOnLoad, isIdleExpired, markActive } from "./idle-lock";
import { useSession } from "./session";

let pending: Promise<void> | null = null;

/**
 * Restores the session from a persisted token — the one-shot the router guard awaits before its
 * first decision.
 *
 * **Why the guard must await this rather than fire it and move on:** a reload starts with an
 * empty Pinia store, so `session.isAuthed` reads `false` for an operator who was mid-shift a
 * second ago. If the guard decides on that stale `false` — redirecting to `/login` — before this
 * promise settles, the operator is signed back in a moment later sitting on the wrong screen: the
 * guard already ran, nothing re-runs it, and "a signed-in person has no use for `/login`" bounces
 * them to `HOME[role]` instead of back to where they were. Awaiting here, before that decision, is
 * what closes the gap: the guard's first evaluation already reflects the true state.
 *
 * **Rehydration means "restore the token, then ask `/v1/me` who this is"** — never decode it.
 * Reads the token straight off disk (`getStoredToken`, independent of the in-memory module
 * variable, which is always `null` this early), loads it into `apiFetch` via `setToken`, and lets
 * `GET /v1/me` be the authority on role and Casino, exactly like `useAuth.login` does.
 *
 * **A stale or rejected token clears itself; anything else leaves it alone.** `STAFF_TOKEN_INVALID`
 * (this API's 401 for "missing, malformed, expired or unknown") means the token is dead — a token
 * that 401s and stays on disk 401s forever, so it is cleared, storage included. `UPSTREAM_UNAVAILABLE`
 * (502, play-svc or device-svc not answering) and a network failure are "unknown, try again later":
 * clearing on an upstream blip would sign out an entire floor during a backend hiccup, so the token
 * survives for the next reload — or the next `storage` event, see `app/storage-sync.ts` — to retry.
 * Branches on `ApiError.code`, never on the bare HTTP status, for the same reason
 * `login-error.ts`'s `classifyLoginError` does: several codes on this API share one status.
 *
 * **A token past the idle window is refused before any request** — see `idle-lock.ts`.
 *
 * **A no-op, resolved immediately, when there is nothing to do** — no stored token, or a session
 * already adopted (`useAuth.login` just set one, or this already ran once this page load) — so
 * calling it costs nothing on the navigations that follow the first. Memoized in `pending`: this
 * is a one-shot per page load, on purpose (see CLAUDE.md's "no in-process retry loops") — a failed
 * attempt does not retry itself; `resetRehydration` is the deliberate, event-driven exception the
 * cross-tab `storage` listener needs, not a general retry mechanism.
 */
export function rehydrateSession(): Promise<void> {
  if (pending) return pending;

  const session = useSession();
  if (session.isAuthed) {
    pending = Promise.resolve();
    return pending;
  }

  const stored = getStoredToken();
  if (!stored) {
    pending = Promise.resolve();
    return pending;
  }

  // The idle lock (`idle-lock.ts`) outranks persistence. A token whose last recorded activity is
  // older than the idle window is dropped unread — never sent to `/v1/me` — so walking away and
  // reloading later does not quietly sign the next person in as the one who left.
  if (isIdleExpired()) {
    setToken(null);
    clearActivity();
    flagLockedOnLoad();
    pending = Promise.resolve();
    return pending;
  }

  setToken(stored);
  pending = backofficeWhoami()
    .then((me) => {
      markActive(Date.now(), true);
      session.adopt({
        id: me.staffId,
        username: me.username,
        role: me.role,
        casinoId: me.casinoId,
      });
      // A reload keeps the Casino a platform_admin had entered in this tab (break-glass.ts).
      if (me.role === "platform_admin") useBreakGlass().restore(me.staffId);
    })
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.code === "STAFF_TOKEN_INVALID") {
        setToken(null);
      }
    });

  return pending;
}

/**
 * Lets a fresh `rehydrateSession()` call actually run again, instead of returning the page load's
 * already-settled memo.
 *
 * The one caller is `app/storage-sync.ts`: when another tab changes the stored token, the fact
 * this tab resolved at page load no longer describes reality, and there is no later "reload" event
 * to naturally re-trigger it.
 */
export function resetRehydration(): void {
  pending = null;
}
